import localforage from "localforage";
import { DateTime } from "luxon";

const lf = localforage.createInstance({
  name: "mem-client",
  storeName: "voice-uploads",
});

export type ResumableUploadRecord = {
  url: string;
  fileSize: number;
  chunkSize: number;
  mimeType?: string;
  // ISO timestamp; legacy records may have number (epoch ms)
  updatedAt: string | number;
};

const KEY_PREFIX = "voice-upload:";

// TTL for resumable upload sessions - 7 days to align with typical GCS signed URL expiry
const UPLOAD_SESSION_TTL_DAYS = 7;

export class ResumableUploadStore {
  private static readonly TTL_DAYS = UPLOAD_SESSION_TTL_DAYS;

  /**
   * Checks if an upload session has expired based on its last update time.
   * Sessions expire after 7 days to align with typical GCS signed URL expiry.
   */
  private static isExpired(updatedAt: string | number | undefined | null): boolean {
    if (!updatedAt && updatedAt !== 0) return true;

    const lastUpdateTime =
      typeof updatedAt === "number"
        ? DateTime.fromMillis(updatedAt)
        : DateTime.fromISO(String(updatedAt));

    if (!lastUpdateTime.isValid) return true;

    const expirationTime = lastUpdateTime.plus({ days: this.TTL_DAYS });
    return DateTime.utc() > expirationTime;
  }

  /**
   * Retrieves a resumable upload session record.
   * Returns null if the session doesn't exist or has expired.
   * Automatically cleans up expired sessions.
   */
  static async get(capturedAudioId: string): Promise<ResumableUploadRecord | null> {
    try {
      const key = KEY_PREFIX + capturedAudioId;
      const record = await lf.getItem<ResumableUploadRecord>(key);

      if (!record || typeof record.url !== "string") {
        return null;
      }

      if (this.isExpired(record.updatedAt)) {
        // Clean up expired session
        await lf.removeItem(key);
        return null;
      }

      return record;
    } catch {
      return null;
    }
  }

  /**
   * Stores a resumable upload session record with current timestamp.
   * Silently ignores storage errors to prevent upload failures.
   */
  static async set(capturedAudioId: string, record: ResumableUploadRecord): Promise<void> {
    try {
      const recordWithTimestamp: ResumableUploadRecord = {
        ...record,
        updatedAt: DateTime.utc().toISO(),
      };
      await lf.setItem(KEY_PREFIX + capturedAudioId, recordWithTimestamp);
    } catch {
      // Silently ignore storage errors to prevent upload failures
    }
  }

  /**
   * Updates the timestamp of an existing upload session to prevent expiration.
   * Used during long uploads to keep the session alive.
   */
  static async bump(capturedAudioId: string): Promise<void> {
    try {
      const key = KEY_PREFIX + capturedAudioId;
      const record = await lf.getItem<ResumableUploadRecord>(key);

      if (!record) return;

      record.updatedAt = DateTime.utc().toISO();
      await lf.setItem(key, record);
    } catch {
      // Silently ignore storage errors
    }
  }

  /**
   * Removes a resumable upload session record.
   * Called when upload completes or is permanently cancelled.
   */
  static async clear(capturedAudioId: string): Promise<void> {
    try {
      await lf.removeItem(KEY_PREFIX + capturedAudioId);
    } catch {
      // Silently ignore storage errors
    }
  }
}
